// src/components/Opinie.jsx
// Oceny gwiazdkowe i opinie o strzelnicach.
//
// Świadome ograniczenia:
//  - opinie ładują się PO stronie klienta, więc nie trafiają do prerenderu.
//    Tak ma być: treść od użytkowników zmienia się w oderwaniu od builda,
//    a wstrzykiwanie jej do statycznego HTML-a dawałoby dane nieaktualne.
//  - publikujemy bez moderacji wstępnej (decyzja właściciela). Dlatego przy
//    każdej opinii jest zgłoszenie nadużycia - to jedyny mechanizm, który
//    pozwala zdjąć wpis o cudzej firmie, gdy okaże się nieprawdziwy.
//  - dopóki opinii jest garstka, nie publikujemy danych strukturalnych
//    AggregateRating - gwiazdki w wynikach Google wymagają realnego wolumenu
//    i naraziłyby serwis na karę za wprowadzanie w błąd.
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import styles from '../css/modules/Opinie.module.css';
import { trackEvent } from '../lib/analytics';
import { odmien, OPINIE_FORMS } from '../lib/odmiana';

const API = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');

const OpinieContext = createContext({ ratings: {}, loaded: false, refresh: () => {} });

/* Jedno pobranie podsumowań na stronę - karta nie odpytuje API osobno,
   bo na wykazie województwa potrafi ich być kilkadziesiąt. */
export const OpinieProvider = ({ children }) => {
  const [ratings, setRatings] = useState({});
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(() => {
    if (!API) return;
    fetch(`${API}/strzelnice/opinie`, { headers: { Accept: 'application/json' } })
      .then(res => (res.ok ? res.json() : null))
      .then(data => {
        if (!data) return;
        setRatings(data.ratings || {});
        setLoaded(true);
      })
      .catch(() => {
        /* brak ocen nie może psuć strony z danymi kontaktowymi */
      });
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <OpinieContext.Provider value={{ ratings, loaded, refresh }}>{children}</OpinieContext.Provider>
  );
};

export const useOpinie = () => useContext(OpinieContext);

const STARS = [1, 2, 3, 4, 5];

export const Gwiazdki = ({ value, size = 'sm' }) => (
  <span
    className={`${styles.stars} ${size === 'lg' ? styles.starsLg : ''}`}
    aria-label={`Ocena ${value} na 5`}
  >
    {STARS.map(n => (
      <span key={n} className={n <= Math.round(value) ? styles.on : styles.off} aria-hidden="true">
        ★
      </span>
    ))}
  </span>
);

const GwiazdkiInput = ({ value, onChange }) => (
  <div className={styles.starInput} role="radiogroup" aria-label="Twoja ocena">
    {STARS.map(n => (
      <button
        key={n}
        type="button"
        role="radio"
        aria-checked={value === n}
        aria-label={`${n} z 5`}
        className={n <= value ? styles.on : styles.off}
        onClick={() => onChange(n)}
      >
        ★
      </button>
    ))}
  </div>
);

/* Zgłoszenie nadużycia. Bez moderacji wstępnej to jedyny sygnał, po którym
   właściciel serwisu może zdjąć nieprawdziwy wpis o cudzej firmie. */
const ZglosOpinie = ({ id }) => {
  const [sent, setSent] = useState(false);
  if (sent) return <span className={styles.reported}>Zgłoszone do sprawdzenia.</span>;
  return (
    <button
      type="button"
      className={styles.report}
      onClick={() => {
        setSent(true);
        fetch(`${API}/strzelnice/opinia/zglos`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id, reason: 'zgłoszenie ze strony' }),
        }).catch(() => {});
      }}
    >
      Zgłoś opinię
    </button>
  );
};

/* Panel pod kartą obiektu: średnia, lista opinii i formularz. */
const OpiniePanel = ({ rangeId, rangeName }) => {
  const { ratings, loaded, refresh } = useOpinie();
  const summary = ratings[rangeId];
  const [open, setOpen] = useState(false);
  const [reviews, setReviews] = useState(null);
  const [rating, setRating] = useState(0);
  const [text, setText] = useState('');
  const [nick, setNick] = useState('');
  const [www, setWww] = useState(''); // pułapka na boty, ukryta przed człowiekiem
  const [status, setStatus] = useState(null);
  const [sending, setSending] = useState(false);

  const loadReviews = () => {
    if (!API || reviews) return;
    fetch(`${API}/strzelnice/opinie?ids=${encodeURIComponent(rangeId)}`)
      .then(res => (res.ok ? res.json() : null))
      .then(data => setReviews((data && data.ratings[rangeId]?.reviews) || []))
      .catch(() => setReviews([]));
  };

  const toggle = () => {
    setOpen(o => {
      if (!o) loadReviews();
      return !o;
    });
  };

  const submit = event => {
    event.preventDefault();
    if (sending) return;
    if (!rating) {
      setStatus({ ok: false, message: 'Zaznacz ocenę w gwiazdkach.' });
      return;
    }
    setSending(true);
    setStatus(null);
    fetch(`${API}/strzelnice/opinia`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ rangeId, rangeName, rating, text, nick, www }),
    })
      .then(async res => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.message || 'Nie udało się zapisać opinii.');
        setStatus({ ok: true, message: data.message });
        setText('');
        setRating(0);
        setNick('');
        trackEvent('review_submit', { label: rangeName });
        refresh();
      })
      .catch(err => setStatus({ ok: false, message: err.message }))
      .finally(() => setSending(false));
  };

  /* Panel pojawia się dopiero, gdy API odpowie. Dzięki temu w prerenderowanym
     HTML-u nie ląduje kilkaset identycznych zdań o braku opinii ani liczba ocen,
     która i tak zdążyłaby się zdezaktualizować przed kolejnym buildem. */
  if (!loaded) return null;

  return (
    <div className={styles.panel}>
      <button type="button" className={styles.toggle} onClick={toggle} aria-expanded={open}>
        {summary ? (
          <>
            <Gwiazdki value={summary.average} />
            <span className={styles.avg}>{summary.average.toFixed(1)}</span>
            <span className={styles.count}>
              ({summary.count} {odmien(summary.count, OPINIE_FORMS)})
            </span>
          </>
        ) : (
          <span className={styles.empty}>Brak opinii - oceń jako pierwszy</span>
        )}
        <span className={styles.chevron} aria-hidden="true">
          {open ? '−' : '+'}
        </span>
      </button>

      {open && (
        <div className={styles.body}>
          {reviews === null && <p className={styles.info}>Wczytuję opinie…</p>}
          {reviews !== null && reviews.length === 0 && (
            <p className={styles.info}>
              Nikt jeszcze nie opisał tej strzelnicy. Twoja opinia będzie pierwsza.
            </p>
          )}
          {reviews !== null && reviews.length > 0 && (
            <ul className={styles.list}>
              {reviews.map(r => (
                <li key={r.id}>
                  <div className={styles.head}>
                    <Gwiazdki value={r.rating} />
                    <span className={styles.nick}>{r.nick}</span>
                    <time dateTime={r.createdAt}>{r.createdAt.slice(0, 10)}</time>
                  </div>
                  <p>{r.text}</p>
                  <ZglosOpinie id={r.id} />
                </li>
              ))}
            </ul>
          )}

          <form className={styles.form} onSubmit={submit}>
            <p className={styles.formTitle}>Dodaj własną opinię</p>
            <GwiazdkiInput value={rating} onChange={setRating} />
            <label>
              <span>Podpis (opcjonalnie)</span>
              <input
                type="text"
                value={nick}
                maxLength={40}
                onChange={e => setNick(e.target.value)}
                placeholder="np. Marek z Krakowa"
              />
            </label>
            <label>
              <span>Twoja opinia</span>
              <textarea
                value={text}
                minLength={30}
                maxLength={1500}
                rows={4}
                required
                onChange={e => setText(e.target.value)}
                placeholder="Jak wyglądała wizyta? Obsługa, dostępne osie, rezerwacja, sprzęt."
              />
            </label>
            <input
              type="text"
              className={styles.hp}
              tabIndex={-1}
              autoComplete="off"
              value={www}
              onChange={e => setWww(e.target.value)}
              aria-hidden="true"
            />
            <button type="submit" className={styles.submit} disabled={sending}>
              {sending ? 'Wysyłam…' : 'Wyślij opinię'}
            </button>
            {status && (
              <p className={status.ok ? styles.ok : styles.error} role="status">
                {status.message}
              </p>
            )}
            <p className={styles.rules}>
              Opinie sprawdzamy przed publikacją. Piszemy o własnym doświadczeniu, bez linków,
              danych kontaktowych i personalnych wycieczek pod adresem pracowników.
            </p>
          </form>
        </div>
      )}
    </div>
  );
};

export default OpiniePanel;
